// Propositions: the Maw's dark deals (docs/04 §15, docs/05 §7). Boon and Fine Print both last the
// run unless stated. Immediate effects run through a DealCtx; lasting ones are modifier hooks.
import { B } from './balance';
import { defineDeal, type DealDef } from './defs';

const eyes = (c: { mods: { features: Set<string> } }) => c.mods.features.has('eyes');
const mulTag = (m: { valueXByTag: Partial<Record<string, number>> }, tag: string, x: number) =>
  void (m.valueXByTag[tag] = (m.valueXByTag[tag] ?? 1) * x);

export const DEAL_LIST: DealDef[] = [
  defineDeal({
    id: 'p_blood',
    requires: (c) => c.mods.features.has('nudge'),
    boon: { mods: (m) => void (m.nudgePerWindow += 2) },
    fine: { mods: (m) => void (m.nudgeCoinCost += B.BLOOD_NUDGE_COST) },
  }),
  defineDeal({
    id: 'p_gluttony',
    boon: { mods: (m) => void m.xMult.push({ source: 'p_gluttony', x: 2 }) },
    fine: { mods: (m) => void (m.hungerGainX *= 2) },
  }),
  defineDeal({
    id: 'p_borrowedluck',
    boon: { mods: (m) => void (m.luck += 5) },
    fine: { mods: (m) => void (m.forcedDeadEvery = B.BORROWED_LUCK_EVERY) },
  }),
  defineDeal({
    id: 'p_eyes',
    requires: eyes,
    boon: { mods: (m) => mulTag(m, 'eye', 3) },
    fine: { now: (c) => c.addDoom(2) },
  }),
  defineDeal({
    id: 'p_tithe',
    boon: { mods: (m) => void (m.tributeMult *= 1 - B.TITHE) },
    fine: { mods: (m) => void (m.tributeSoulX *= 1 - B.TITHE) },
  }),
  defineDeal({
    id: 'p_sweettooth',
    requires: eyes,
    boon: { now: (c) => c.addSymbol('goldtooth', 1) },
    fine: { now: (c) => c.addSymbol('tooth', 2) },
  }),
  defineDeal({
    id: 'p_sparehands',
    requires: (c) => c.mods.features.has('hold'),
    boon: { mods: (m) => void (m.holdPerWindow += 3) },
    fine: { mods: (m) => void m.features.delete('nudge') },
  }),
  defineDeal({
    id: 'p_rotpact',
    requires: (c) => c.mods.symbolGates.has('rot'),
    boon: { mods: (m) => mulTag(m, 'fruit', 3) },
    fine: { mods: (m) => void (m.rotSlow *= 0.5) },
  }),
  defineDeal({
    id: 'p_loan',
    story: true,
    boon: { now: (c) => c.addCoins(c.nextTribute) },
    fine: { now: (c) => c.flag('loanNext', B.LOAN_TRIBUTE_MULT) },
  }),
  defineDeal({
    id: 'p_skeletonkey',
    requires: (c) => c.hasShop,
    boon: { now: (c) => c.flag('freeShop', 1) },
    fine: { now: (c) => c.flag('crumble', 1) },
  }),
  defineDeal({
    id: 'p_twinreel',
    boon: { now: (c) => void c.duplicateBest(B.TWIN_COPIES) },
    fine: { now: (c) => c.destroyRandom(B.TWIN_COPIES, 'p_twinreel') },
  }),
  defineDeal({
    id: 'p_hexaddict',
    requires: (c) => c.mods.symbolGates.has('hex'),
    boon: { mods: (m) => void (m.cleanseMult = Math.max(m.cleanseMult, B.HEX_ADDICT_CLEANSE)) },
    fine: { now: (c) => c.addSymbol('hex', 2) },
  }),
  defineDeal({
    id: 'p_longnight',
    boon: { now: (c) => c.startNightShift(B.LONG_NIGHT_SPINS) },
    fine: { now: (c) => c.nextWindowSpins(-3) },
  }),
  defineDeal({
    id: 'p_feedme',
    boon: { now: (c) => c.setHunger(0) },
    fine: { now: (c) => c.destroyRandom(2, 'maw') },
  }),
  defineDeal({
    id: 'p_handcuffs',
    requires: (c) => c.hasShop,
    boon: { now: (c) => c.giveCharm('L') },
    fine: { mods: (m) => void (m.charmSlots -= 1) },
  }),
  defineDeal({
    id: 'p_bitterpill',
    requires: (c) => c.mods.symbolGates.has('hex'),
    boon: {
      now: (c) => {
        c.clearHexes();
        c.flag('hexImmune', B.BITTER_PILL_WINDOWS);
      },
    },
    fine: { mods: (m) => void (m.luck -= 3) },
  }),
  defineDeal({
    id: 'p_doublesoul',
    boon: { mods: (m) => void (m.soulMult *= 2) },
    fine: { mods: (m) => void (m.payEarlyDisabled = true) },
  }),
  defineDeal({
    id: 'p_grin',
    boon: { now: (c) => c.addSymbol('grin', 2) },
    fine: {
      mods: (m) => {
        if (m.features.has('eyes')) m.doomFloor = Math.max(m.doomFloor, 2);
        else m.hungerFloor = Math.max(m.hungerFloor, B.GRIN_HUNGER_FLOOR);
      },
    },
  }),
  defineDeal({
    id: 'p_candle',
    boon: { mods: (m) => void (m.spinsPerWindow += 2) },
    fine: { mods: (m) => void (m.hungerGainPct += 0.5) },
  }),
  defineDeal({
    id: 'p_covenant',
    covenant: true,
    requires: (c) => c.meta.lifetime.dealsAccepted >= B.COVENANT_DEALS && !c.meta.flags.covenant,
    boon: { now: (c) => c.flag('covenant', 1) },
    fine: {},
  }),
];
